import React, { useMemo, useState } from "react";
import {
  Pencil,
  Trash2,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  Briefcase,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 8;

// Show a date like "12 Mar 2025".
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// A job is soft-deleted when the API includes a deleted_at timestamp.
const isDeleted = (job) => !!job.deleted_at;

// A job is active whether the API sends true, 1, "1" or "active".
const isActive = (job) =>
  job.is_active === true ||
  job.is_active === 1 ||
  job.is_active === "1" ||
  job.is_active === "active";

const JobsTable = ({
  jobs = [],
  loading = false,
  onToggleStatus,
  onView,
  onEdit,
  onDelete,
}) => {
  const [query, setQuery] = useState("");
  const [engagement, setEngagement] = useState("all");
  const [page, setPage] = useState(1);
  // Column sort — newest first by default.
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  // Engagement options taken from the data.
  const engagementOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => j.engagement && set.add(j.engagement));
    return [...set];
  }, [jobs]);

  // Filter by search query + engagement.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((j) => {
      const title = (j.job_title || "").toLowerCase();
      const role = (j.role || "").toLowerCase();
      const loc = (j.location || "").toLowerCase();
      const eng = j.engagement || "";
      const matchesQuery =
        !q || title.includes(q) || role.includes(q) || loc.includes(q);
      const matchesEngagement = engagement === "all" || eng === engagement;
      return matchesQuery && matchesEngagement;
    });
  }, [jobs, query, engagement]);

  // Value used for comparing two rows on the active sort column.
  const sortValue = (job, key) => {
    switch (key) {
      case "title":
        return (job.job_title || "").toLowerCase();
      case "role":
        return (job.role || "").toLowerCase();
      case "location":
        return (job.location || "").toLowerCase();
      case "type":
        return (job.engagement || "").toLowerCase();
      case "date":
        return new Date(job.created_at || 0).getTime();
      case "status":
        return isActive(job) ? 1 : 0;
      default:
        return "";
    }
  };

  // Sort the filtered rows by the active column + direction.
  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Click a header: flip direction if it's the active column, else sort it ascending.
  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
    setPage(1);
  };

  const sortIcon = (key) => {
    if (sort.key !== key)
      return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sort.dir === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const handleEngagement = (value) => {
    setEngagement(value);
    setPage(1);
  };

  const thClass =
    "text-gray-500 font-semibold uppercase text-[11px] tracking-wider";

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={handleSearch}
            placeholder="Search by title, role or location…"
            className="w-full rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition"
          />
        </div>

        <Select value={engagement} onValueChange={handleEngagement}>
          <SelectTrigger className="w-[180px] h-auto rounded-md border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {engagementOptions.map((e) => (
              <SelectItem key={e} value={e}>
                {e}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto shrink-0 text-sm text-gray-500">
          {filtered.length} {filtered.length === 1 ? "job" : "jobs"}
        </span>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b border-gray-200">
            <TableHead className={`${thClass} pl-6`}>
              <button
                type="button"
                onClick={() => toggleSort("title")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Title
                {sortIcon("title")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("role")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Role
                {sortIcon("role")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("location")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Location
                {sortIcon("location")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("type")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Type
                {sortIcon("type")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("date")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Date
                {sortIcon("date")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("status")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Status
                {sortIcon("status")}
              </button>
            </TableHead>
            <TableHead className={`${thClass} text-right pr-6`}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-gray-500 py-12">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading jobs…
                </span>
              </TableCell>
            </TableRow>
          ) : pageItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-12">
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <Briefcase className="h-8 w-8" />
                  <span className="text-sm">
                    {query || engagement !== "all"
                      ? "No jobs match your filters."
                      : "No jobs yet. Click “Add Job” to create one."}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            pageItems.map((job) => {
              const title = job.job_title || "Untitled";
              const active = isActive(job);
              const deleted = isDeleted(job);

              return (
                <TableRow
                  key={job.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                >
                  {/* Title — click to view */}
                  <TableCell className="font-medium text-gray-900 pl-6 py-4">
                    <button
                      type="button"
                      onClick={() => onView?.(job)}
                      className="text-left hover:text-[#b32126] transition-colors"
                    >
                      {title}
                    </button>
                  </TableCell>

                  {/* Role */}
                  <TableCell className="text-gray-600 py-4">
                    {job.role || <span className="text-gray-400">—</span>}
                  </TableCell>

                  {/* Location */}
                  <TableCell className="py-4">
                    {job.location ? (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                        {job.location}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Type / engagement */}
                  <TableCell className="py-4">
                    {job.engagement ? (
                      <span className="inline-flex items-center rounded-full bg-[#b32126]/10 px-2.5 py-0.5 text-xs font-medium text-[#b32126]">
                        {job.engagement}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Date */}
                  <TableCell className="text-gray-600 py-4">
                    {formatDate(job.created_at)}
                  </TableCell>

                  {/* Status — toggle, or a Deleted badge for soft-deleted jobs */}
                  <TableCell className="py-4">
                    {deleted ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600">
                        <Trash2 className="h-3 w-3" />
                        Deleted
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onToggleStatus?.(job)}
                        title={active ? "Active" : "Inactive"}
                        className={`relative h-6 w-11 rounded-full transition-colors ${
                          active ? "bg-green-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            active ? "translate-x-5" : ""
                          }`}
                        />
                      </button>
                    )}
                  </TableCell>

                  {/* Actions — edit (always, incl. deleted for restore) + delete */}
                  <TableCell className="py-4 pr-6">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        title={deleted ? "Edit / Restore" : "Edit"}
                        onClick={() => onEdit?.(job)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!deleted && (
                        <Button
                          variant="destructive"
                          size="icon"
                          title="Delete"
                          onClick={() => onDelete?.(job)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-medium transition-colors ${
                  p === currentPage
                    ? "bg-[#b32126] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsTable;
